import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  ListSubheader,
  Typography,
} from "@mui/material";
import { map } from "lodash";
import React from "react";
import theme from "../../assets/theme/theme";
import { useMoveToFolderModalContext } from "../../contexts/MoveToFolderModalContext";
import { IDocument } from "../../firebaseTypes";
import constants from "../../constants";
import { patchData } from "../../utils/fetchUtils";
import { useAuth } from "../../contexts/AuthContext";
import { useUserContext } from "../../contexts/UserContext";
import { getDocName } from "../../utils/documentUtils";

export interface Props {
  document: IDocument;
}

const MoveToFolderModal: React.FC<Props> = (props) => {
  const { openModalState, closeModal } = useMoveToFolderModalContext();
  const [isSavingDoc, setIsSavingDoc] = React.useState(false);
  const [folder, setFolder] = React.useState("");
  const [error, setError] = React.useState("");
  const { currentUser } = useAuth();
  const { user } = useUserContext();

  const moveToAnotherFolder = React.useCallback(async () => {
    setIsSavingDoc(true);
    try {
      if (currentUser) {
        const token = await currentUser?.getIdToken();
        await patchData(
          `${constants.middleHost}/users/${currentUser.uid}/documents/${props.document.id}/`,
          {
            doc_type: folder,
          },
          {
            Authorization: `Bearer ${token}`,
          }
        );
      }
      setIsSavingDoc(false);
      closeModal();
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please retry.");
    }
    setIsSavingDoc(false);
  }, [folder, closeModal, currentUser, props.document.id]);

  const handleChange = React.useCallback(
    (evt) => {
      setFolder(evt.target.value);
    },
    [setFolder]
  );

  return (
    <Dialog
      maxWidth="lg"
      open={openModalState}
      scroll="body"
      onClose={closeModal}
      disableEscapeKeyDown={true}
    >
      <DialogTitle  className="select-document-type-wrapper">
        Select custom folder for
        <Typography 
          className="select-document-popover"
          sx={{
            font: "inherit",
            fontWeight: 600,
            color: theme.palette.primary.main,
            ml: 1,
          }}
          component="span"
        >
          {getDocName(props.document)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ display: "flex", flexWrap: "wrap" }}>
          <FormControl required fullWidth>
            {/* <InputLabel id="folder_select" htmlFor="folder">
              Select Document Type
            </InputLabel> */}
            <Select
              id="folder"
              labelId="folder_select"
              name="folder"
              onChange={handleChange}
              inputProps={{
                name: "folder",
                id: "folder",
              }}
              displayEmpty
              defaultValue=""
              value={folder || ""}
              // input={<DcInput />}
            >
              <MenuItem key="empty-label-doc-type" value="">
                <em>Select Custom Folder</em>
              </MenuItem>
              {/* <ListSubheader disableSticky color="primary">
                Auto-generated Folders
              </ListSubheader>
              {map(folderOptions, (f, i) => {
                return (
                  <MenuItem value={f} key={`${f}-${i}`}>
                    {f}
                  </MenuItem>
                );
              })} */}

              {user?.customFolders && (
                <ListSubheader disableSticky color="primary">
                  Custom Folders
                </ListSubheader>
              )}
              {user?.customFolders &&
                map(user?.customFolders, (cf, i) => {
                  return (
                    <MenuItem value={cf.name} key={`${cf.name}-${i}`}>
                      {cf.name}
                    </MenuItem>
                  );
                })}
              {!user?.customFolders && (
                <MenuItem disabled>
                  <em>No custom folders created yet</em>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
        {error && (
          <Alert
            variant="outlined"
            severity="error"
            sx={{
              mt: 4,
            }}
          >
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={closeModal} disabled={isSavingDoc}>
          Cancel
        </Button>
        <LoadingButton
          loadingPosition="start"
          loading={isSavingDoc}
          autoFocus
          variant="contained"
          onClick={moveToAnotherFolder}
          disabled={!user?.customFolders || folder === ""}
          aria-label="move document to another folder"
        >
          Move
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default MoveToFolderModal;
